import api from "./api";

const offerService = {
  createOffer: async (offerData) => {
    const response = await api.post("/api/v1/offers", offerData);
    return response;
  },

  // Get all offers with filters
  getOffers: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      });

      const response = await api.get(
        `/api/v1/offers?${queryParams.toString()}`
      );

      // Ensure the response has the expected structure
      if (!response.data) {
        throw new Error("Invalid response format from server");
      }

      // Add pagination wrapper if not present
      if (!response.data.data?.docs) {
        response.data = {
          data: {
            docs: response.data.data || [],
            totalDocs: response.data.data?.length || 0,
            limit: 10,
            totalPages: 1,
            page: 1,
            pagingCounter: 1,
            hasPrevPage: false,
            hasNextPage: false,
            prevPage: null,
            nextPage: null,
          },
        };
      }

      return response;
    } catch (error) {
      console.error("Error in getOffers:", error);
      throw error;
    }
  },

  // Get offer by ID
  getOfferById: async (id) => {
    const response = await api.get(`/api/v1/offers/${id}`);
    return response;
  },

  // Update offer
  updateOffer: async (id, updateData) => {
    const response = await api.put(`/api/v1/offers/${id}`, updateData);
    return response;
  },

  // Submit offer for approval
  submitForApproval: async (id, workflowData) => {
    const response = await api.post(
      `/api/v1/offers/${id}/submit-for-approval`,
      workflowData
    );
    return response;
  },

  // Approve offer
  approveOffer: async (id, approvalData) => {
    const response = await api.post(
      `/api/v1/offers/${id}/approve`,
      approvalData
    );
    return response;
  },

  // Send offer to candidate
  sendOffer: async (id) => {
    const response = await api.post(`/api/v1/offers/${id}/send`);
    return response;
  },

  // Resend offer email
  resendOffer: async (id) => {
    const response = await api.post(`/api/v1/offers/${id}/resend`);
    return response;
  },

  // Withdraw offer
  withdrawOffer: async (id, reason) => {
    const response = await api.post(`/api/v1/offers/${id}/withdraw`, {
      reason,
    });
    return response;
  },

  // Extend offer validity
  extendOffer: async (id, newValidTill, reason) => {
    const response = await api.post(`/api/v1/offers/${id}/extend`, {
      newValidTill,
      reason,
    });
    return response;
  },

  // Get offer statistics
  getOfferStats: async (startDate, endDate) => {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append("startDate", startDate);
    if (endDate) queryParams.append("endDate", endDate);

    const response = await api.get(
      `/api/v1/offers/stats/overview?${queryParams.toString()}`
    );
    return response;
  },

  // Get offer templates
  getTemplates: async () => {
    const response = await api.get("/api/v1/templates");
    return response;
  },

  // Get ATS applications in offer states
  getOfferRelatedApplications: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Add filter for offer-related stages
      queryParams.append("stages", "final_interview,offer_pending");
      queryParams.append("status", "active");

      Object.keys(filters).forEach((key) => {
        if (filters[key] && key !== "currentStage") {
          queryParams.append(key, filters[key]);
        }
      });

      // Get all applications matching the stage criteria
      const response = await api.get(
        `/api/v1/ats/applications?${queryParams.toString()}`
      );

      // If the response doesn't have docs property, wrap it in a pagination-like structure
      if (!response.data.data.docs) {
        response.data.data = {
          docs: response.data.data,
          totalDocs: response.data.data.length,
          limit: response.data.data.length,
          totalPages: 1,
          page: 1,
        };
      }

      return response;
    } catch (error) {
      console.error("Error fetching offer-related applications:", error);
      throw error;
    }
  },

  // Get combined offer data (offers + offer-related applications)
  getAllOfferData: async (filters = {}) => {
    try {
      // Add authorization header if token exists
      const token = localStorage.getItem("token");
      if (token) {
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      }

      // Get transformed data from backend
      const response = await api.get(`/api/v1/offers/all`);

      // Ensure the response has the expected structure
      if (!response.data?.data) {
        throw new Error("Invalid response format from server");
      }

      // The backend now returns the properly structured data
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error("Error fetching combined offer data:", error);
      throw error;
    }
  },

  // Accept offer (public route)
  acceptOffer: async (token, signature, comments, ipAddress, userAgent) => {
    const response = await api.post(`/api/v1/offers/accept/${token}`, {
      signature,
      comments,
      ipAddress,
      userAgent,
    });
    return response;
  },

  // Decline offer (public route)
  declineOffer: async (token, comments, ipAddress, userAgent) => {
    const response = await api.post(`/api/v1/offers/decline/${token}`, {
      comments,
      ipAddress,
      userAgent,
    });
    return response;
  },

  // Get public offer details (for candidate preview)
  getPublicOffer: async (token) => {
    const response = await api.get(`/api/v1/offers/public/${token}`);
    return response;
  },
};

export default offerService;
